import { createElement, useState, useCallback, useMemo } from 'react';
import Raven from 'raven-js';
import { trackPotentialBannerInteraction, mapPurchaseMotionApiName } from 'ui-addon-upgrades/ums/utils';
import { isUndefined } from 'ui-addon-upgrades/utils';
import { getIsRetailPortal } from '../common/api/fetchRetail';
import { prefetchMeetingData } from '../common/api/meetingHelpers';
import { getParsedProductPurchaseMotions } from '../common/data/purchaseMotionData/getParsedProductPurchaseMotions';
import { sourceKeys } from '../common/data/upgradeData/properties/sources';
import { getPropertyRepInfo } from '../common/data/upgradeData/transformers/getPropertyRepInfo';
import { getSource } from '../common/data/upgradeData/transformers/getPropertySource';
import { getIsFreePortalAndOwnedProducts } from '../common/eligibility/getIsFreePortal';
import { TALK_TO_SALES_BUTTON } from '../common/constants/PurchaseMotionLocations';
import { TALK_TO_SALES, TALK_TO_SALES_FALLBACK } from '../purchaseMotions/PurchaseMotionTypes';
import { trackPurchaseMotionFailure } from '../utils/metricUtils';
import { overrideAssignable, trackCommModalClose } from './utils';
import CommModalContainer from './CommModalContainer';
export const useContactSalesAction = props => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const upgradeDataWithApiName = useMemo(() => {
    const {
      upgradeData
    } = props;
    return Object.assign({}, upgradeData, {
      apiName: mapPurchaseMotionApiName(!upgradeData.apiName, upgradeData.upgradeProduct, 'ContactSalesButton', upgradeData.apiName),
      source: getSource({
        app: upgradeData.app,
        screen: upgradeData.screen,
        uniqueId: upgradeData.uniqueId
      }, sourceKeys.CONTACT_SALES_BUTTON)
    });
  }, [props]);
  const executeAction = useCallback(async () => {
    setIsLoading(true);
    try {
      var _repInfoPromise$value;
      const {
        allowModal
      } = props;
      const isAssignable = overrideAssignable(props.isAssignable);
      let ttsPurchaseMotion;
      if (isUndefined(isAssignable) && upgradeDataWithApiName.apiName) {
        try {
          const purchaseMotions = await getParsedProductPurchaseMotions({
            location: TALK_TO_SALES_BUTTON,
            apiName: upgradeDataWithApiName.apiName
          });
          ttsPurchaseMotion = purchaseMotions && purchaseMotions[0] || TALK_TO_SALES_FALLBACK;
        } catch (_unused) {
          ttsPurchaseMotion = TALK_TO_SALES_FALLBACK;
        }
      }
      const computedIsAssignable = isAssignable || ttsPurchaseMotion === TALK_TO_SALES;
      const {
        pointType,
        upgradeKey
      } = upgradeDataWithApiName;
      try {
        await trackPotentialBannerInteraction(pointType, upgradeKey);
      } catch (error) {
        Raven.captureException(new Error('Failed to submit interaction from handleContactSalesAction'), {
          extra: {
            message: error.message,
            uniqueId: upgradeDataWithApiName.uniqueId
          }
        });
      }
      const [repInfoPromise, isFreePortalPromise, isRetailPortalPromise] = await Promise.allSettled([getPropertyRepInfo(upgradeDataWithApiName, allowModal), getIsFreePortalAndOwnedProducts(), getIsRetailPortal()]);
      let repInfo = {};
      let isFreePortal = true;
      let isRetailPortal = false;
      if (repInfoPromise.status === 'fulfilled' && (_repInfoPromise$value = repInfoPromise.value) !== null && _repInfoPromise$value !== void 0 && _repInfoPromise$value.repInfo) {
        repInfo = repInfoPromise.value.repInfo;
      }
      if (isFreePortalPromise.status === 'fulfilled') {
        var _isFreePortalPromise$, _isFreePortalPromise$2;
        isFreePortal = (_isFreePortalPromise$ = (_isFreePortalPromise$2 = isFreePortalPromise.value) === null || _isFreePortalPromise$2 === void 0 ? void 0 : _isFreePortalPromise$2.isFreePortal) !== null && _isFreePortalPromise$ !== void 0 ? _isFreePortalPromise$ : true;
      }
      if (isRetailPortalPromise.status === 'fulfilled') {
        var _isRetailPortalPromis;
        isRetailPortal = (_isRetailPortalPromis = isRetailPortalPromise.value) !== null && _isRetailPortalPromis !== void 0 ? _isRetailPortalPromis : false;
      }
      if (repInfoPromise.status === 'rejected' || isFreePortalPromise.status === 'rejected' || isRetailPortalPromise.status === 'rejected') {
        trackPurchaseMotionFailure({
          uniqueId: upgradeDataWithApiName.uniqueId,
          apiName: upgradeDataWithApiName.apiName,
          purchaseMotion: TALK_TO_SALES
        });
        Raven.captureException(new Error('handleContactSalesAction opened without setting data'));
      }
      if (props.onTrackClick) {
        props.onTrackClick(Object.assign({}, props, {
          upgradeData: upgradeDataWithApiName,
          isAssignable: computedIsAssignable
        }), isRetailPortal, repInfo);
      }
      prefetchMeetingData().catch(error => {
        Raven.captureException(new Error('Failed to prefetchMeetingData from handleContactSalesAction'), {
          extra: {
            error
          }
        });
      });
      const result = {
        repInfo,
        isFreePortal,
        isRetailPortal,
        isAssignable: computedIsAssignable,
        upgradeDataWithApiName,
        ttsPurchaseMotion: ttsPurchaseMotion
      };
      setActionResult(result);
      setIsOpen(true);
    } catch (error) {
      Raven.captureException(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsLoading(false);
    }
  }, [props, upgradeDataWithApiName]);
  const handleClose = useCallback(closeInfo => {
    if (actionResult) {
      trackCommModalClose(actionResult.upgradeDataWithApiName, actionResult.repInfo);
    }
    setIsOpen(false);
    if (props.onClose) {
      props.onClose(closeInfo);
    }
  }, [actionResult, props]);
  const modalElement = isOpen && actionResult && !isLoading ? /*#__PURE__*/createElement(CommModalContainer, {
    handleClose,
    isFreePortal: actionResult.isFreePortal,
    isRetailPortal: actionResult.isRetailPortal,
    isAssignable: actionResult.isAssignable,
    modalRoot: props.modalRoot,
    upgradeData: actionResult.upgradeDataWithApiName,
    repInfo: actionResult.repInfo,
    _overrideCommunicationMethodKey: props._overrideCommunicationMethodKey,
    _overrideModalHeaderText: props._overrideModalHeaderText,
    isUpgradePoint: props.isUpgradePoint,
    isGD0532ExperimentVariation: props.isGD0532ExperimentVariation
  }) : null;
  return {
    executeAction,
    isLoading,
    isOpen,
    modalElement,
    actionResult
  };
};